/**
 * Work Lykke hands to a page's own agent when she opens it: "make a deck about X" goes to the Deck studio, a brief
 * dropped on her goes to Order a campaign. The page takes it once when it mounts. In memory only (files cannot go in
 * storage, and a handoff older than two minutes is stale), so a reload never replays it.
 *
 * Pure TypeScript.
 */

export interface KickoffHandoff { text: string; files: File[] }
export interface Handoffs { kickoff: KickoffHandoff }
type Kind = keyof Handoffs;

const TTL_MS = 120_000;
const store = new Map<Kind, { at: number; value: unknown }>();
const listeners = new Set<(kind: Kind) => void>();

export function giveHandoff<K extends Kind>(kind: K, value: Handoffs[K]): void {
  store.set(kind, { at: Date.now(), value });
  for (const l of listeners) l(kind);
}

/** The waiting handoff of this kind (and forget it), or null. */
export function takeHandoff<K extends Kind>(kind: K): Handoffs[K] | null {
  const hit = store.get(kind);
  store.delete(kind);
  if (!hit || Date.now() - hit.at > TTL_MS) return null;
  return hit.value as Handoffs[K];
}

export function hasHandoff(kind: Kind): boolean {
  const hit = store.get(kind);
  return !!hit && Date.now() - hit.at <= TTL_MS;
}

/** A page already open hears a new handoff. Returns the unsubscribe. */
export function onHandoff(fn: (kind: Kind) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Files Lykke reads as a brief (Order a campaign's intake): text, PDF, Word, slides and pictures of a brief. */
export function isBriefFile(f: { name: string; type: string }): boolean {
  return /pdf|msword|officedocument|text\/|rtf|presentation/i.test(f.type) || /\.(pdf|docx?|txt|md|rtf|pptx?|csv)$/i.test(f.name);
}

/** The deck ask for the Deck studio: the person's own words, or Lykke's fuller ask when it was not cut short. */
export function deckAsk(userText: string, prompt?: string): string {
  const p = (prompt || '').trim();
  const u = userText.trim();
  if (p && p.length < 80 && p.length > u.length) return p;
  return u || p;
}
